import api from "@/services/api";
import type { Evaluation, CanEvaluateResponse } from "@/types/Evaluation";
import type { CreateEvaluationDto } from "@/dtos/CreateEvaluationDto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useCreateEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["createEvaluation"],
    mutationFn: async (data: CreateEvaluationDto) => {
      const response = await api.post<Evaluation>("/evaluations", data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["occurrence", variables.occurrenceId] });
      queryClient.invalidateQueries({ queryKey: ["evaluations", variables.occurrenceId] });
      queryClient.invalidateQueries({ queryKey: ["canEvaluate", variables.occurrenceId] });
      queryClient.invalidateQueries({ queryKey: ["pendingEvaluations"] });
    },
  });
};

export const useGetEvaluationsByOccurrence = (occurrenceId: string, cycle?: number) => {
  return useQuery({
    queryKey: ["evaluations", occurrenceId, cycle],
    queryFn: async () => {
      const params = cycle ? `?cycle=${cycle}` : "";
      const response = await api.get<Evaluation[]>(`/evaluations/occurrence/${occurrenceId}${params}`);
      return response.data;
    },
    enabled: !!occurrenceId,
  });
};

export const useGetPendingEvaluations = () => {
  return useQuery({
    queryKey: ["pendingEvaluations"],
    queryFn: async () => {
      const response = await api.get<any[]>("/evaluations/pending");
      return response.data;
    },
  });
};

export const useCanEvaluate = (occurrenceId: string) => {
  return useQuery({
    queryKey: ["canEvaluate", occurrenceId],
    queryFn: async () => {
      const response = await api.get<CanEvaluateResponse>(`/evaluations/can-evaluate/${occurrenceId}`);
      return response.data;
    },
    enabled: !!occurrenceId,
  });
};
